import FadeProducer from '@src/fadeEffectProcessor/FadeProducer';
import { deepClone } from '@src/utility/deepClone';
import { ensureElementZPositions } from '@src/utility/general';
import {
  computeStackedLayoutScaleForHeight,
  findLayoutRelatedElements,
  pickUnderlyingVideoElement,
} from '@src/utility/stackedLayout';
import { makeAutoObservable, toJS } from 'mobx';
import { v4 as uuid } from 'uuid';

import { mapToElementState } from '../../videoTranscriptionProcessor/utils';

import type VideoCreatorStore from '../VideoCreatorStore';
import type { ElementState } from '@src/renderer/ElementState';
import type { ExtraElementData } from '@src/types.ts/story';

class StackedLayoutManager {
  store: VideoCreatorStore;

  constructor(store: VideoCreatorStore) {
    makeAutoObservable(this, {
      store: false,
    });
    this.store = store;
  }

  get videoSourceStore() {
    return this.store.rootStore.videoSourceStore;
  }

  async addLayoutElements(
    elementId: string,
    type: string,
    label: string = 'setting layout element state',
  ): Promise<void> {
    await this.store.reframingModeManager.exitReframingMode();

    const videoSourceStore = this.store.rootStore.videoSourceStore;

    const source = videoSourceStore.source;
    const element = source.elements.find((el: any) => el.id === elementId);
    if (!element) {
      return;
    }
    const eed = this.store.currentVideo?.extraElementData[elementId];

    // Use high track numbers for layout compositions to avoid conflicts
    const LAYOUT_TRACKS_BASE = 90; // Start layout tracks at track 90

    // Check if we already have layout composition tracks in use
    let highestLayoutTrack = LAYOUT_TRACKS_BASE - 1;

    // Find all existing compositions to identify layout tracks already in use
    for (const el of source.elements) {
      if (el.type === 'composition') {
        const elEed = this.store.currentVideo?.extraElementData[
          el.id
        ] as ExtraElementData;
        if (
          elEed &&
          (elEed.layoutPosition === 'top' || elEed.layoutPosition === 'bottom')
        ) {
          // This is a layout composition element, track its track number
          if (el.track > highestLayoutTrack) {
            highestLayoutTrack = el.track;
          }
        }
      }
    }

    // Determine tracks for our new layout compositions
    const bottomTrack = highestLayoutTrack + 1;
    const topTrack = highestLayoutTrack + 2;

    const width = '100%';
    const x = '50%';

    let topElementId = uuid();
    let bottomElementId = uuid();
    const topHeight = type === 'stacked-1-1' ? '50%' : '33.333%';
    const bottomHeight = type === 'stacked-1-1' ? '50%' : '66.666%';
    const topScale = computeStackedLayoutScaleForHeight(topHeight);
    const bottomScale = computeStackedLayoutScaleForHeight(bottomHeight);

    let topElement = {
      id: topElementId,
      track: topTrack,
      type: 'composition',
      time: element.time,
      duration: element.duration,
      x,
      y: type === 'stacked-1-1' ? '25%' : '16.666%',
      width,
      height: topHeight,
      volume: 0,
      clip: true,
      locked: false,
      z_index: -1000, // Force elements to appear behind regular tracks
      elements: [
        {
          ...element,
          duration: null,
          time: 0,
          width: topScale,
          height: topScale,
          // LEFT aligned for top split
          x: '0%',
          x_anchor: '0%',
          x_alignment: '0%',
          y: '50%',
          visible: true,
          locked: false,
          z_index: 0, // Ensure child elements have proper z-index
        },
      ],
    };

    let bottomElement = {
      id: bottomElementId,
      track: bottomTrack,
      type: 'composition',
      time: element.time,
      duration: element.duration,
      x,
      y: type === 'stacked-1-1' ? '75%' : '66.666%',
      width,
      height: bottomHeight,
      volume: 0,
      clip: true,
      locked: false,
      z_index: -1000, // Force elements to appear behind regular tracks
      elements: [
        {
          ...element,
          duration: null,
          time: 0,
          width: bottomScale,
          height: bottomScale,
          // RIGHT aligned for bottom split
          x: '100%',
          x_anchor: '100%',
          x_alignment: '100%',
          y: '50%',
          visible: true,
          locked: false,
          volume: '0', // Ensure child video has no volume
          z_index: 0, // Ensure child elements have proper z-index
        },
      ],
    };

    source.elements.push(topElement);
    source.elements.push(bottomElement);

    // We'll need to apply locked and visible state via modifications after setting the source

    this.store.currentVideo!.extraElementData = {
      ...this.store.currentVideo!.extraElementData,
      [topElementId]: {
        ...eed,
        layoutType: type,
        layoutAssociatedIds: [elementId],
        layoutPosition: 'top',
      },
      [bottomElementId]: {
        ...eed,
        layoutType: type,
        layoutAssociatedIds: [elementId],
        layoutPosition: 'bottom',
      },
      [elementId]: {
        ...eed,
        layoutType: type,
        layoutAssociatedIds: [topElementId, bottomElementId],
        layoutPosition: 'hidden',
      },
    };

    // Ensure all elements have z_index set to match tracks
    const updatedSource = ensureElementZPositions(source);

    // Set the source with our modified elements
    videoSourceStore.setSource(updatedSource, false);

    // After setting the source, apply locked and visible modifications properly
    await this.store.applyVideoStateModifications(
      {
        [`${elementId}.locked`]: true,
        [`${elementId}.visible`]: false,
      },
      true,
      label,
    );

    await this.store.setActiveElements(topElementId);
  }

  /**
   * element can be either non-stacked layout element - return undefined
   * or one of the following stacked layout elements:
   * 1. hidden parent element - return itself
   * 2. top/child composition - return itself
   * 3. top/child inner video - return outer composition
   */
  getLayoutElementFor(element: ElementState): ElementState | undefined {
    const layoutInfo = findLayoutRelatedElements(this.store, element.source.id);
    // hidden parent or top/child composition - return itself
    if (layoutInfo.isHiddenParent || layoutInfo.isLayoutChild) {
      return element;
    }

    // need to check if the element is an inner video element of some layout composition
    if (element.source.type === 'video') {
      const childComposition = this.videoSourceStore.source.elements?.find(
        (e: Record<string, any>) => {
          if (e.type !== 'composition') {
            return false;
          }
          const layoutInfo = findLayoutRelatedElements(
            this.store,
            e.id as string,
          );
          if (!layoutInfo.isLayoutChild) {
            return false;
          }
          return (
            Array.isArray(e?.elements) &&
            e?.elements.some(
              (innerEl: Record<string, unknown>) =>
                innerEl.id === element.source.id,
            )
          );
        },
      );
      // inner video - return outer composition
      if (childComposition) {
        return mapToElementState(childComposition);
      }
    }

    // not a stacked layout - return undefined
    return undefined;
  }

  isLayoutElement(element: ElementState): boolean {
    return !!this.getLayoutElementFor(element);
  }

  isLayoutElementActive(element: ElementState): boolean {
    const layoutElement = this.getLayoutElementFor(element);
    if (!layoutElement) {
      return false;
    }
    const layoutInfo = findLayoutRelatedElements(
      this.store,
      layoutElement.source.id,
    );
    const layoutElementIds = [
      ...layoutInfo.layoutChildrenIds,
      layoutInfo.hiddenParentId,
      // need to include inner videos
      ...(this.videoSourceStore.source?.elements || [])
        .filter((el: Record<string, any>) =>
          layoutInfo.layoutChildrenIds.includes(el.id),
        )
        .flatMap((el: Record<string, any>) =>
          (el.elements || []).map((inner: Record<string, any>) => inner.id),
        ),
    ].filter((id): id is string => id !== null);

    return layoutElementIds.some(id =>
      this.store.activeElementIds.includes(id),
    );
  }

  async deleteElement(
    elementId: string,
    saveSnapshot: boolean = true,
  ): Promise<void> {
    await this.store.reframingModeManager.exitReframingMode();

    const intialSource = deepClone(this.videoSourceStore.source);

    let newSource = {
      ...intialSource,
      elements: this.videoSourceStore.source.elements.filter(
        (el: any) => el.id !== elementId,
      ),
    };

    if (saveSnapshot) {
      this.store.saveVideoStateSnapshot(undefined, `deleting element`);
    }

    // Set source by the mutated state
    newSource =
      this.store.videoTranscriptionProcessor.adjustTrackNumbersToStartFromOne(
        newSource,
      );

    // Ensure all elements have proper z-index values
    const updatedSource = ensureElementZPositions(newSource);

    this.videoSourceStore.setSource(updatedSource, saveSnapshot);
    this.store.trackManager.updateFrameLockedTracksAfterRearrange(
      intialSource.elements,
      this.videoSourceStore.source.elements,
    );
  }

  async cutCurrentTrack(activeElement: ElementState) {
    const layoutElements = findLayoutRelatedElements(
      this.store,
      activeElement.source.id,
    );
    if (!layoutElements.isHiddenParent && !layoutElements.isLayoutChild) {
      return;
    }

    const source = this.videoSourceStore.source;

    const allIds = [
      layoutElements.hiddenParentId,
      ...layoutElements.layoutChildrenIds,
    ].filter((id): id is string => id !== null);
    const elementSourcesWithIndex = allIds
      .map(id => {
        const idx = source.elements.findIndex((el: any) => el.id === id);
        return { idx, element: source.elements[idx] };
      })
      .filter(({ idx }) => idx !== -1);

    if (elementSourcesWithIndex.length === 0) {
      return;
    }

    this.store.saveVideoStateSnapshot(
      undefined,
      `cutting stacked layout track`,
    );

    const fadeProducer = new FadeProducer(this.store, activeElement);
    const newElements: {
      idx: number;
      head: any;
      tail: any;
      originalId: string;
    }[] = [];

    for (const { idx, element: elementSource } of elementSourcesWithIndex) {
      if (
        activeElement.globalTime > this.store.time ||
        activeElement.globalTime + activeElement.duration < this.store.time
      ) {
        continue;
      }

      const tailElementSource = {
        ...deepClone(elementSource),
        id: uuid(),
        audio_fade_in: 0,
        animations: elementSource.animations?.filter(
          (a: any) => a.time === 'end',
        ),
      };
      const headElementSource = {
        ...deepClone(elementSource),
        id: uuid(),
        audio_fade_out: 0,
        animations: elementSource.animations?.filter(
          (a: any) => a.time === 'start' || a.time === 0,
        ),
      };
      headElementSource.duration =
        this.store.time - (activeElement.globalTime || 0);
      tailElementSource.time = this.store.time;
      tailElementSource.duration =
        activeElement.duration - headElementSource.duration;

      // Get the trim_start from the underlying video element for proper calculation
      const sourceElementForTrim = pickUnderlyingVideoElement(elementSource);

      const startTrimValue =
        parseFloat(sourceElementForTrim?.trim_start || '0') || 0;
      const trimStart =
        startTrimValue + (this.store.time - (activeElement.globalTime || 0));

      const tailSourceForTrim = pickUnderlyingVideoElement(tailElementSource);
      if (tailSourceForTrim) {
        tailSourceForTrim.trim_start = trimStart;
      }

      fadeProducer.processCrossfadeOnElementCut(
        headElementSource,
        tailElementSource,
      );

      newElements.push({
        idx,
        head: headElementSource,
        tail: tailElementSource,
        originalId: elementSource.id,
      });
    }

    newElements.sort((a, b) => b.idx - a.idx);
    for (const { idx, head, tail } of newElements) {
      source.elements.splice(idx, 1, head, tail);
    }

    const updatedSource = ensureElementZPositions(source);
    this.videoSourceStore.setSource(updatedSource, true);
    await fadeProducer.processCrossfadeOnOriginalVideoCut();

    this.store.currentVideo!.extraElementData = newElements.reduce(
      (acc, newEl) => {
        const oldEed = acc?.[newEl.originalId] as ExtraElementData | null;

        if (!oldEed?.layoutType) {
          return acc;
        }

        let layoutAssociatedIdsForHead: string[];
        let layoutAssociatedIdsForTail: string[];

        if (oldEed.layoutPosition === 'hidden') {
          layoutAssociatedIdsForHead = newElements
            .filter((el: any) => el.head.id !== newEl.head.id)
            .map((el: any) => el.head.id);
          layoutAssociatedIdsForTail = newElements
            .filter((el: any) => el.tail.id !== newEl.tail.id)
            .map((el: any) => el.tail.id);
        } else {
          layoutAssociatedIdsForHead = [
            newElements.find(
              (el: any) => el.originalId === layoutElements.hiddenParentId,
            )?.head.id,
          ];
          layoutAssociatedIdsForTail = [
            newElements.find(
              (el: any) => el.originalId === layoutElements.hiddenParentId,
            )?.tail.id,
          ];
        }

        acc[newEl.head.id] = {
          ...(acc[newEl.head.id] || {}),
          layoutType: oldEed.layoutType,
          layoutPosition: oldEed.layoutPosition,
          layoutAssociatedIds: layoutAssociatedIdsForHead,
        };
        acc[newEl.tail.id] = {
          ...(acc[newEl.tail.id] || {}),
          layoutType: oldEed.layoutType,
          layoutPosition: oldEed.layoutPosition,
          layoutAssociatedIds: layoutAssociatedIdsForTail,
        };

        delete oldEed?.layoutType;
        delete oldEed?.layoutPosition;
        delete oldEed?.layoutAssociatedIds;

        return acc;
      },
      toJS(this.store.currentVideo?.extraElementData || {}),
    );

    const tailToActivate =
      newElements.find(el => el.originalId !== layoutElements.hiddenParentId)
        ?.tail.id ||
      (newElements.length ? newElements[newElements.length - 1].tail.id : null);
    if (tailToActivate) {
      await this.store.setActiveElements(tailToActivate);
    }
  }

  extendModificationsToAllElements(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
  ): Record<string, any> | null {
    const layoutElements = findLayoutRelatedElements(
      this.store,
      element.source.id,
    );
    if (!layoutElements.isHiddenParent && !layoutElements.isLayoutChild) {
      return null;
    }

    const source = this.videoSourceStore.source;

    const otherIds = [
      layoutElements.hiddenParentId,
      ...layoutElements.layoutChildrenIds,
    ]
      .filter((id): id is string => id !== null)
      .filter(id => id !== element.source.id);

    const otherElements = otherIds
      .map(id => {
        return source.elements.find((el: any) => el.id === id);
      })
      .filter(Boolean);

    if (otherElements.length === 0) {
      return null;
    }

    let modifications: Record<string, any> = {};

    for (const elementSource of otherElements) {
      if (placement.globalTime !== elementSource.time) {
        modifications[`${elementSource.id}.time`] = placement.globalTime;
      }
      if (placement.duration !== elementSource.duration) {
        modifications[`${elementSource.id}.duration`] = placement.duration;
      }
      const el = pickUnderlyingVideoElement(elementSource);
      if (el && placement.trimStart !== el.trim_start) {
        modifications[`${el.id}.trim_start`] = placement.trimStart;
      }
    }

    return modifications;
  }

  async deleteElementWithTranscription(element: ElementState) {
    const layoutElements = findLayoutRelatedElements(
      this.store,
      element.source.id,
    );
    if (!layoutElements.isHiddenParent && !layoutElements.isLayoutChild) {
      return null;
    }

    this.store.saveVideoStateSnapshot(
      undefined,
      `removing stacked layout element`,
    );

    // delete child elements
    for (const childId of layoutElements.layoutChildrenIds) {
      if (this.store.currentVideo!.extraElementData[childId]) {
        delete this.store.currentVideo!.extraElementData[childId];
      }
      await this.deleteElement(childId, false);
    }

    // delete parent element
    const { hiddenParentId } = layoutElements;
    if (!hiddenParentId) {
      return;
    }
    if (this.store.currentVideo!.extraElementData[hiddenParentId]) {
      delete this.store.currentVideo!.extraElementData[hiddenParentId];
    }
    await this.store.deleteElementWithTranscription(hiddenParentId, false);
  }

  getInnerElementToBecomeActive(element: ElementState): ElementState {
    const layoutInfo = findLayoutRelatedElements(this.store, element.source.id);
    if (!layoutInfo.isLayoutChild && !layoutInfo.isHiddenParent) {
      return element;
    }

    let result: ElementState | undefined;
    let childComposition: ElementState | undefined;

    if (layoutInfo.isHiddenParent) {
      // case 1: parent - find first child composition
      childComposition = this.store.renderer?.findElement(
        el => el.source.id === layoutInfo.layoutChildrenIds?.[0],
      );
    } else {
      // case 2: top or bottom element: already a composition
      childComposition = element;
    }

    if (childComposition?.source.type === 'composition') {
      result = this.store.renderer?.findElement(
        el => el.source.type === 'video',
        childComposition,
      );
    }

    return result || element;
  }

  reselectInnerElement(activeElement: ElementState): void {
    const innerElement = this.getInnerElementToBecomeActive(activeElement);

    if (innerElement.source.id === activeElement.source.id) {
      return;
    }

    this.store.setActiveElements(innerElement.source.id);
  }
}

export default StackedLayoutManager;
